import { useState } from 'react';
import { FileText, Loader2, Landmark, Info, AlertCircle } from 'lucide-react';
import {
  formatUsd,
  isValidRoutingNumber,
  hasPlausibleRoutingPrefix,
  isPlausibleAccountNumber,
} from '@ach/shared';

import { publicApi, ApiError } from '../lib/api.js';
import type { PayData } from './PayApp.js';

type Mode = 'saved' | 'new';

/**
 * The invoices, then the bank form, then the authorization.
 *
 * One page rather than a wizard. A customer with a checkbook in front of them
 * wants to see what they are paying and pay it; splitting that across three
 * screens adds two chances to abandon and hides the total at the moment they
 * are asked to authorize a debit for it.
 */
export function Review({
  token,
  data,
  onSubmitted,
}: {
  token: string;
  data: PayData;
  onSubmitted: (reference: string) => void;
}) {
  const hasSaved = data.savedAccounts.length > 0;
  const [mode, setMode] = useState<Mode>(hasSaved ? 'saved' : 'new');
  const [savedId, setSavedId] = useState(data.savedAccounts[0]?.id ?? '');

  const [bankName, setBankName] = useState('');
  const [holder, setHolder] = useState(data.customerName);
  const [accountType, setAccountType] = useState<'checking' | 'savings'>('checking');
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [accountConfirm, setAccountConfirm] = useState('');

  const [authorized, setAuthorized] = useState(false);
  const [signature, setSignature] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const routingDigits = routing.replace(/\D/g, '');
  const routingLooksDone = routingDigits.length === 9;
  const routingBad =
    routingLooksDone && (!isValidRoutingNumber(routing) || !hasPlausibleRoutingPrefix(routing));
  const accountsDiffer =
    accountConfirm !== '' && account.replace(/\D/g, '') !== accountConfirm.replace(/\D/g, '');

  const canSubmit =
    authorized &&
    signature.trim().length >= 2 &&
    (mode === 'saved'
      ? savedId !== ''
      : bankName.trim().length >= 2 &&
        holder.trim().length >= 2 &&
        isValidRoutingNumber(routing) &&
        hasPlausibleRoutingPrefix(routing) &&
        isPlausibleAccountNumber(account) &&
        !accountsDiffer &&
        accountConfirm !== '');

  /** The authorization text with its placeholders filled, exactly as recorded. */
  const authorizationText = data.authorization.body
    .replace('{{AMOUNT}}', formatUsd(data.totalAmountCents))
    .replace('{{INVOICES}}', data.invoices.map((i) => i.invoiceNumber).join(', '))
    .replace('{{DATE}}', new Date().toLocaleDateString('en-US'))
    .replace('{{NAME}}', signature.trim() || '—');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    const bank =
      mode === 'saved'
        ? { mode: 'saved' as const, savedAccountId: savedId }
        : {
            mode: 'new' as const,
            bankName: bankName.trim(),
            accountHolderName: holder.trim(),
            accountType,
            routingNumber: routing.trim(),
            accountNumber: account.trim(),
            accountNumberConfirm: accountConfirm.trim(),
          };

    try {
      const result = await publicApi.post<{ submitted: boolean; reference: string }>(
        `/api/pay/${token}/submit`,
        {
          bank,
          authorizationAccepted: true,
          authorizationTextId: data.authorization.id,
          signatureName: signature.trim(),
        },
      );
      onSubmitted(result.reference);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        if (e.fields) {
          // Flatten the discriminated-union paths (bank.routingNumber → routingNumber).
          const flat: Record<string, string> = {};
          for (const [path, message] of Object.entries(e.fields)) {
            flat[path.split('.').pop() ?? path] = message;
          }
          setFieldErrors(flat);
        }
      } else {
        setError('Something went wrong. Nothing was submitted — please try again.');
      }
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <section className="card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4">
          <h1 className="font-semibold text-slate-900">
            {data.invoices.length === 1 ? 'Your invoice' : 'Your invoices'}
          </h1>
          <p className="text-sm text-slate-500">{data.customerName}</p>
        </div>

        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-2 font-medium">Invoice</th>
              <th className="px-5 py-2 font-medium">Date</th>
              <th className="px-5 py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="px-5 py-3">
                  <span className="font-medium text-slate-900">{invoice.invoiceNumber}</span>
                  {invoice.poNumber && (
                    <span className="ml-2 text-slate-500">PO {invoice.poNumber}</span>
                  )}
                  {invoice.hasPdf && (
                    <a
                      href={`/api/pay/${token}/invoice/${invoice.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 inline-flex items-center gap-1 text-xs text-gold-700 hover:underline"
                    >
                      <FileText className="h-3 w-3" aria-hidden />
                      View PDF
                    </a>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-600">{invoice.invoiceDate ?? '—'}</td>
                <td className="px-5 py-3 text-right font-medium text-slate-900">
                  {formatUsd(invoice.amountCents)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200">
              <td colSpan={2} className="px-5 py-3 font-semibold text-slate-900">
                Total to pay
              </td>
              <td className="px-5 py-3 text-right text-xl font-bold text-slate-900">
                {formatUsd(data.totalAmountCents)}
              </td>
            </tr>
          </tfoot>
        </table>

        {data.noteToCustomer && (
          <p className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-700">
            {data.noteToCustomer}
          </p>
        )}
      </section>

      <form onSubmit={submit} className="space-y-5">
        <section className="card p-5">
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <Landmark className="h-4 w-4 text-gold-600" aria-hidden />
            Your bank account
          </h2>

          {hasSaved && (
            <div className="mt-4 space-y-2">
              {data.savedAccounts.map((saved) => (
                <label
                  key={saved.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                    mode === 'saved' && savedId === saved.id
                      ? 'border-gold-400 bg-gold-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="account"
                    className="h-4 w-4 text-gold-600 focus:ring-gold-500"
                    checked={mode === 'saved' && savedId === saved.id}
                    onChange={() => {
                      setMode('saved');
                      setSavedId(saved.id);
                    }}
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">{saved.bankName}</span>{' '}
                    <span className="text-slate-600">
                      {saved.accountType === 'savings' ? 'savings' : 'checking'} ending{' '}
                      <span className="font-account">{saved.accountLast4}</span>
                    </span>
                  </span>
                </label>
              ))}

              <label
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                  mode === 'new' ? 'border-gold-400 bg-gold-50' : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <input
                  type="radio"
                  name="account"
                  className="h-4 w-4 text-gold-600 focus:ring-gold-500"
                  checked={mode === 'new'}
                  onChange={() => setMode('new')}
                />
                <span className="text-sm font-medium text-slate-900">Use a different account</span>
              </label>
            </div>
          )}

          {mode === 'new' && (
            <div className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="bank">
                    Bank name
                  </label>
                  <input
                    id="bank"
                    className={`field-input ${fieldErrors.bankName ? 'field-input-error' : ''}`}
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    autoComplete="off"
                    required
                  />
                  {fieldErrors.bankName && <p className="field-error">{fieldErrors.bankName}</p>}
                </div>
                <div>
                  <label className="field-label" htmlFor="holder">
                    Name on the account
                  </label>
                  <input
                    id="holder"
                    className="field-input"
                    value={holder}
                    onChange={(e) => setHolder(e.target.value)}
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              <fieldset>
                <legend className="field-label">Account type</legend>
                <div className="flex gap-3">
                  {(['checking', 'savings'] as const).map((type) => (
                    <label
                      key={type}
                      className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border p-2.5 text-sm font-medium capitalize transition ${
                        accountType === type
                          ? 'border-gold-400 bg-gold-50 text-gold-900'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        className="h-4 w-4 text-gold-600 focus:ring-gold-500"
                        checked={accountType === type}
                        onChange={() => setAccountType(type)}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label className="field-label" htmlFor="routing">
                  Routing number
                </label>
                <input
                  id="routing"
                  className={`field-input font-account ${
                    routingBad || fieldErrors.routingNumber ? 'field-input-error' : ''
                  }`}
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="9 digits"
                  value={routing}
                  onChange={(e) => setRouting(e.target.value)}
                  autoComplete="off"
                  required
                />
                {routingBad ? (
                  <p className="field-error">
                    That routing number doesn't look right — check the nine digits at the bottom
                    left of a check.
                  </p>
                ) : fieldErrors.routingNumber ? (
                  <p className="field-error">{fieldErrors.routingNumber}</p>
                ) : (
                  <p className="mt-1 text-xs text-slate-500">
                    The nine digits at the bottom left of a check.
                  </p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="account">
                    Account number
                  </label>
                  <input
                    id="account"
                    className={`field-input font-account ${
                      fieldErrors.accountNumber ? 'field-input-error' : ''
                    }`}
                    inputMode="numeric"
                    maxLength={20}
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    autoComplete="off"
                    required
                  />
                  {fieldErrors.accountNumber && (
                    <p className="field-error">{fieldErrors.accountNumber}</p>
                  )}
                </div>
                <div>
                  <label className="field-label" htmlFor="accountConfirm">
                    Account number again
                  </label>
                  <input
                    id="accountConfirm"
                    className={`field-input font-account ${accountsDiffer ? 'field-input-error' : ''}`}
                    inputMode="numeric"
                    maxLength={20}
                    value={accountConfirm}
                    onChange={(e) => setAccountConfirm(e.target.value)}
                    // Pasting the same wrong value twice defeats the check, so
                    // this field is typed rather than pasted.
                    onPaste={(e) => e.preventDefault()}
                    autoComplete="off"
                    required
                  />
                  {accountsDiffer && <p className="field-error">These do not match.</p>}
                </div>
              </div>

              <p className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                An incorrect account number means the payment is returned by your bank, usually
                with a fee. It is worth checking against a check or your online banking.
              </p>
            </div>
          )}
        </section>

        <section className="card p-5">
          <h2 className="font-semibold text-slate-900">Authorize the payment</h2>

          <div className="mt-3 max-h-56 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
            {authorizationText}
          </div>

          <label className="mt-4 flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-gold-600 focus:ring-gold-500"
              checked={authorized}
              onChange={(e) => setAuthorized(e.target.checked)}
            />
            <span className="text-sm text-slate-800">
              I have read the above and I authorize Gold Star Printing to debit{' '}
              <strong>{formatUsd(data.totalAmountCents)}</strong> from this account.
            </span>
          </label>

          <div className="mt-4 max-w-sm">
            <label className="field-label" htmlFor="signature">
              Type your full name to sign
            </label>
            <input
              id="signature"
              className="field-input"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              autoComplete="name"
              required
            />
          </div>

          <p className="mt-4 text-xs text-slate-500">
            The date, your name, your IP address and the exact wording above are recorded with this
            authorization.
          </p>
        </section>

        {error && (
          <div
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div>{error}</div>
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <button type="submit" className="btn-primary w-full max-w-sm py-3" disabled={busy || !canSubmit}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Submit payment details
          </button>
          <p className="text-center text-xs text-slate-500">
            Nothing is taken from your account now. We schedule the debit with our bank afterwards,
            and it usually posts within one to three banking days.
          </p>
        </div>
      </form>
    </div>
  );
}
